import React, { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowRight, Save, Info, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Edit Relationship dialog — extracted from SemanticModelsPage.
 * Owns no state beyond what's controlled by the parent; mutations flow
 * back through the provided setters and `onSaved` callback.
 */
export function EditRelationshipDialog({
  open, onOpenChange,
  editRel, setEditRel,
  entities, setEntities,
  setRelationships,
  model,
}) {
  // Local join-key mappings (multi-column). Seeded from the relationship's
  // stored `join_conditions` array, falling back to the legacy single
  // source_field/target_field pair. Hooks MUST run before any early return.
  const [mappings, setMappings] = useState([{ source: '', target: '' }]);
  useEffect(() => {
    if (!editRel) return;
    const jc = Array.isArray(editRel.join_conditions) ? editRel.join_conditions : [];
    if (jc.length) {
      setMappings(jc.map(c => ({ source: c.source_attr || '', target: c.target_attr || '' })));
    } else {
      setMappings([{ source: editRel.source_field || '', target: editRel.target_field || '' }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRel?.relationship_id, open]);

  const setMapping = (idx, key, val) =>
    setMappings(prev => prev.map((m, i) => (i === idx ? { ...m, [key]: val } : m)));
  const addMapping = () => setMappings(prev => [...prev, { source: '', target: '' }]);
  const removeMapping = (idx) => setMappings(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  if (!editRel) {
    return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="hidden" /></Dialog>;
  }
  const srcEntity = entities.find(en => en.entity_id === editRel.source_entity_id);
  const tgtEntity = entities.find(en => en.entity_id === editRel.target_entity_id);
  const srcAttrs = srcEntity ? (srcEntity.attributes || []) : [];
  const tgtAttrs = tgtEntity ? (tgtEntity.attributes || []) : [];
  // Join-key dropdowns must show the FIELD NAME (physical column / display name),
  // never the free-text description. Also always include the relationship's
  // current join key as an option so an existing join never renders blank when
  // the entity's attribute list doesn't contain that exact name.
  const fieldLabelOf = (a) => a.display_name || a.name;
  const withCurrent = (attrs, currents) => {
    const list = [...attrs];
    for (const current of currents) {
      if (current && !list.some(a => a.name === current)) list.unshift({ name: current, display_name: current });
    }
    return list;
  };
  const srcOptions = withCurrent(srcAttrs, mappings.map(m => m.source));
  const tgtOptions = withCurrent(tgtAttrs, mappings.map(m => m.target));

  const loadColumnsForEntity = async (entityId, entity) => {
    if (!model?.source_id || !entity) return;
    try {
      const res = await api.describeTable(model.source_id, { table_name: entity.table_name || entity.name });
      if (res.columns?.length > 0) {
        const attrs = res.columns.map(c => ({
          name: c.name, description: c.name.replace(/_/g, ' '),
          data_type: 'string', semantic_type: 'dimension',
        }));
        await api.updateSemanticEntity(entityId, { attributes: attrs });
        setEntities(prev => prev.map(e => e.entity_id === entityId ? { ...e, attributes: attrs } : e));
        toast.success(`Loaded ${attrs.length} columns`);
      }
    } catch (err) { toast.error(err.message); }
  };

  const save = async () => {
    try {
      const valid = mappings.filter(m => m.source && m.target);
      const join_conditions = valid.map(m => ({ source_attr: m.source, target_attr: m.target }));
      const first = valid[0] || { source: editRel.source_field || '', target: editRel.target_field || '' };
      const updated = await api.updateSemanticRelationship(editRel.relationship_id, {
        name: editRel.name, join_type: editRel.join_type, cardinality: editRel.cardinality,
        source_field: first.source, target_field: first.target, join_conditions,
        is_bidirectional: editRel.is_bidirectional, is_active: editRel.is_active, role: editRel.role,
        line_style: editRel.line_style || 'dashed',
        line_color: editRel.line_color || 'default',
      });
      setRelationships(prev => prev.map(r => r.relationship_id === editRel.relationship_id ? updated : r));
      onOpenChange(false);
      toast.success('Relationship updated');
    } catch (err) { toast.error(err.message); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setEditRel(null); }}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Edit Relationship</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-[10px] text-muted-foreground">Name</Label>
            <Input value={editRel.name || ''} onChange={e => setEditRel(r => ({ ...r, name: e.target.value }))} className="rounded-lg bg-muted/50 border-border/50 mt-0.5" />
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
              <p className="text-[8px] text-emerald-600 dark:text-emerald-400 uppercase tracking-wider font-semibold mb-0.5">Source (FROM)</p>
              <p className="text-xs font-medium text-foreground truncate">{srcEntity ? srcEntity.name : editRel.source_entity_id}</p>
            </div>
            <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
              <ArrowRight className="w-4 h-4 text-primary" />
              <button onClick={() => { setEditRel(r => ({ ...r, source_entity_id: r.target_entity_id, target_entity_id: r.source_entity_id })); setMappings(prev => prev.map(m => ({ source: m.target, target: m.source }))); }} className="text-[8px] px-1.5 py-0.5 rounded bg-muted/30 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors" title="Swap source and target">Swap</button>
            </div>
            <div className="flex-1 min-w-0 p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-center">
              <p className="text-[8px] text-blue-400 uppercase tracking-wider font-semibold mb-0.5">Target (TO)</p>
              <p className="text-xs font-medium text-foreground truncate">{tgtEntity ? tgtEntity.name : editRel.target_entity_id}</p>
            </div>
          </div>
          {/* Join key mappings — multi-column join predicates ANDed together.
              Each row is a source↔target field pair; add rows for composite keys. */}
          <div className="space-y-2" data-testid="edit-join-mappings">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] text-muted-foreground">Field Mappings (joined with AND)</Label>
              <button
                type="button"
                onClick={addMapping}
                data-testid="edit-add-join-mapping-btn"
                className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
              >
                <Plus className="w-3 h-3" />Add mapping
              </button>
            </div>
            {mappings.map((m, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_auto_1fr_auto] items-end gap-2" data-testid={`edit-join-mapping-row-${idx}`}>
                <div className="min-w-0">
                  {idx === 0 && <Label className="text-[10px] text-emerald-600 dark:text-emerald-400">Source Field</Label>}
                  <Select value={m.source} onValueChange={v => setMapping(idx, 'source', v)}>
                    <SelectTrigger data-testid={`edit-join-source-field-${idx}`} className="rounded-lg bg-muted/50 border-border/50 mt-0.5 min-w-0 [&>span]:truncate"><SelectValue placeholder="Select field..." /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {srcAttrs.length > 0
                        ? srcOptions.map(a => <SelectItem key={a.name} value={a.name}>{fieldLabelOf(a)}</SelectItem>)
                        : <div className="px-3 py-2 text-xs text-muted-foreground">{model?.source_id ? <button onClick={() => loadColumnsForEntity(editRel.source_entity_id, srcEntity)} className="text-primary hover:underline">Click to load columns</button> : 'No columns available'}</div>}
                    </SelectContent>
                  </Select>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-primary flex-shrink-0 mb-2.5" />
                <div className="min-w-0">
                  {idx === 0 && <Label className="text-[10px] text-blue-400">Target Field</Label>}
                  <Select value={m.target} onValueChange={v => setMapping(idx, 'target', v)}>
                    <SelectTrigger data-testid={`edit-join-target-field-${idx}`} className="rounded-lg bg-muted/50 border-border/50 mt-0.5 min-w-0 [&>span]:truncate"><SelectValue placeholder="Select field..." /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {tgtAttrs.length > 0
                        ? tgtOptions.map(a => <SelectItem key={a.name} value={a.name}>{fieldLabelOf(a)}</SelectItem>)
                        : <div className="px-3 py-2 text-xs text-muted-foreground">{model?.source_id ? <button onClick={() => loadColumnsForEntity(editRel.target_entity_id, tgtEntity)} className="text-primary hover:underline">Click to load columns</button> : 'No columns available'}</div>}
                    </SelectContent>
                  </Select>
                </div>
                <button
                  type="button"
                  onClick={() => removeMapping(idx)}
                  disabled={mappings.length <= 1}
                  data-testid={`edit-remove-join-mapping-${idx}`}
                  className="mb-1 p-1 rounded text-muted-foreground hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Remove mapping"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] text-muted-foreground">Join Type</Label>
              <Select value={editRel.join_type || 'inner'} onValueChange={v => setEditRel(r => ({ ...r, join_type: v }))}>
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inner">Inner Join</SelectItem>
                  <SelectItem value="left">Left Outer Join</SelectItem>
                  <SelectItem value="right">Right Outer Join</SelectItem>
                  <SelectItem value="full">Full Outer Join</SelectItem>
                  <SelectItem value="cross">Cross Join</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Cardinality</Label>
              <Select value={editRel.cardinality || 'many_to_one'} onValueChange={v => setEditRel(r => ({ ...r, cardinality: v }))}>
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_to_one">1:1 One to One</SelectItem>
                  <SelectItem value="one_to_many">1:N One to Many</SelectItem>
                  <SelectItem value="many_to_one">N:1 Many to One</SelectItem>
                  <SelectItem value="many_to_many">N:N Many to Many</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1 mb-1">
              <Label className="text-[10px] text-muted-foreground">Line style</Label>
              <Tooltip><TooltipTrigger asChild><span className="cursor-help"><Info className="w-3 h-3 text-muted-foreground/70 hover:text-muted-foreground" /></span></TooltipTrigger><TooltipContent side="right" className="max-w-[260px]"><p className="text-xs leading-relaxed">Pick how this relationship is drawn on the canvas. Useful for distinguishing primary vs supporting joins, or different relationship roles at a glance.</p></TooltipContent></Tooltip>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {[
                { value: 'solid', label: 'Solid', dash: '' },
                { value: 'dashed', label: 'Dashed', dash: '6 3' },
                { value: 'dotted', label: 'Dotted', dash: '2 4' },
                { value: 'dashed_thin', label: 'Thin', dash: '3 5' },
              ].map(opt => {
                const isActive = (editRel.line_style || 'dashed') === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setEditRel(r => ({ ...r, line_style: opt.value }))}
                    className={`group flex flex-col items-center gap-1 px-2 py-2 rounded-lg border transition-colors ${isActive ? 'border-primary/50 bg-primary/10' : 'border-border/40 hover:border-border/70 bg-muted/20'}`}
                    data-testid={`line-style-${opt.value}`}
                  >
                    <svg viewBox="0 0 60 8" className="w-full h-2">
                      <line x1="0" y1="4" x2="60" y2="4" stroke="currentColor" strokeWidth="2" strokeDasharray={opt.dash}
                        className={isActive ? 'text-primary' : 'text-muted-foreground'} />
                    </svg>
                    <span className={`text-[9px] ${isActive ? 'text-primary font-medium' : 'text-muted-foreground'}`}>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Line color — paired with line_style for distinguishing many
              overlapping joins. Curated palette keeps it on-aesthetic. */}
          <div>
            <div className="flex items-center gap-1 mb-1">
              <Label className="text-[10px] text-muted-foreground">Line color</Label>
              <Tooltip><TooltipTrigger asChild><span className="cursor-help"><Info className="w-3 h-3 text-muted-foreground/70 hover:text-muted-foreground" /></span></TooltipTrigger><TooltipContent side="right" className="max-w-[260px]"><p className="text-xs leading-relaxed">Color-code joins so overlapping relationships stay readable. Useful for grouping by domain (premiums vs claims) or by role.</p></TooltipContent></Tooltip>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[
                { value: 'default', label: 'Blue',    swatch: 'bg-blue-500/60'    },
                { value: 'cyan',    label: 'Cyan',    swatch: 'bg-cyan-500/70'    },
                { value: 'emerald', label: 'Emerald', swatch: 'bg-emerald-500/70' },
                { value: 'amber',   label: 'Amber',   swatch: 'bg-amber-500/75'   },
                { value: 'violet',  label: 'Violet',  swatch: 'bg-violet-500/70'  },
                { value: 'pink',    label: 'Pink',    swatch: 'bg-pink-500/70'    },
                { value: 'red',     label: 'Red',     swatch: 'bg-red-500/70'     },
                { value: 'slate',   label: 'Slate',   swatch: 'bg-slate-500/65'   },
              ].map(opt => {
                const isActive = (editRel.line_color || 'default') === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setEditRel(r => ({ ...r, line_color: opt.value }))}
                    title={opt.label}
                    aria-label={`Line color: ${opt.label}`}
                    className={`relative w-7 h-7 rounded-full border-2 transition-all flex items-center justify-center ${isActive ? 'border-primary ring-2 ring-primary/20' : 'border-border/40 hover:border-border/70'}`}
                    data-testid={`line-color-${opt.value}`}
                  >
                    <span className={`w-4 h-4 rounded-full ${opt.swatch}`} />
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <Label className="text-[10px] text-muted-foreground">Bi-directional cross-filter</Label>
              <Tooltip><TooltipTrigger asChild><span className="cursor-help"><Info className="w-3 h-3 text-muted-foreground/70 hover:text-muted-foreground" /></span></TooltipTrigger><TooltipContent side="right" className="max-w-[260px]"><p className="text-xs leading-relaxed">When enabled, filters applied on either side of this relationship will propagate to the other side. By default, filters only flow from the dimension (one) side to the fact (many) side. Enable this when both tables need to filter each other, e.g. a bridge table in a many-to-many relationship.</p></TooltipContent></Tooltip>
            </div>
            <Switch checked={editRel.is_bidirectional || false} onCheckedChange={v => setEditRel(r => ({ ...r, is_bidirectional: v }))} />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-[10px] text-muted-foreground">Active relationship</Label>
            <Switch checked={editRel.is_active !== false} onCheckedChange={v => setEditRel(r => ({ ...r, is_active: v }))} />
          </div>
          <div>
            <div className="flex items-center gap-1 mb-0.5">
              <Label className="text-[10px] text-muted-foreground">Role (for role-playing dimensions)</Label>
              <Tooltip><TooltipTrigger asChild><span className="cursor-help"><Info className="w-3 h-3 text-muted-foreground/70 hover:text-muted-foreground" /></span></TooltipTrigger><TooltipContent side="right" className="max-w-[260px]"><p className="text-xs leading-relaxed">Use roles when a single dimension table is joined to the same fact table multiple times with different meanings. For example, a Date dimension joined as both "Order Date" and "Ship Date". Enter a label that distinguishes this specific relationship's purpose.</p></TooltipContent></Tooltip>
            </div>
            <Input value={editRel.role || ''} onChange={e => setEditRel(r => ({ ...r, role: e.target.value }))} placeholder="e.g. Order Date, Ship Date" className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-xs" />
          </div>
          <Button onClick={save} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95"><Save className="w-4 h-4 mr-2" />Save Changes</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
